import React from "react";
import { toastr } from "react-redux-toastr";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import Dropzone from "react-dropzone";
import { FormGroup, FormControl, FormLabel } from "react-bootstrap";
import { FiTrash2 } from "react-icons/fi";
import { FaRegCopy } from "react-icons/fa";
import { FaRegFilePdf } from "react-icons/fa6";
import { documentsService } from "services";
import { errorFormating } from "utils/utils";
import SubmitButton from "components/SubmitButton";
import { storeFoldersActions } from "actions/storeFoldersActions";
import { getAppName } from "utils/utils";
import Switch from "components/Switch";
import { trackHaddockDocumentUpload } from "utils/posthogTracking";

/**
 * El endpoint massive/single puede devolver 200 con un array de filas, o un 4xx/5xx
 * con el mismo detalle por página incrustado en el JSON. Esto ocurre sobre todo con
 * atomic_processing=true: hay que mostrar la tabla igual que en el flujo “exitoso”.
 */
function extractPdfBulkPageResults(raw, depth = 0) {
	if (!raw || depth > 6) return null;

	const looksLikePageRow = (item) =>
		item &&
		typeof item === "object" &&
		(item.status === "SUCCESS" || item.status === "ERROR");

	const normalize = (rows) =>
		rows.map((item) => ({
			...item,
			file_name:
				item.file_name ??
				item.fileName ??
				item.document_name ??
				item.documentName,
		}));

	const asResultArray = (maybeArr) => {
		if (!Array.isArray(maybeArr) || maybeArr.length === 0) return null;
		if (!maybeArr.some(looksLikePageRow)) return null;
		return normalize(maybeArr);
	};

	const direct = asResultArray(raw);
	if (direct) return direct;

	if (typeof raw === "object" && !Array.isArray(raw)) {
		const keys = [
			"results",
			"data",
			"pageResults",
			"processingResults",
			"attachments",
			"bulkResults",
			"processing_results",
			"page_results",
			"content",
		];
		for (const k of keys) {
			const inner = extractPdfBulkPageResults(raw[k], depth + 1);
			if (inner) return inner;
		}
		for (const v of Object.values(raw)) {
			const inner = extractPdfBulkPageResults(v, depth + 1);
			if (inner) return inner;
		}
	}
	return null;
}

class DocumentsAdd extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			isLoading: false,
			submitted: false,
			selectedFolder: -1,
			errors: {},
			files: props.preSelectedFiles || [],
			customNames: {}, // Nuevo estado para manejar nombres personalizados
			employeeIdentifier: "NIF",
			uploadMode: null, // 'multiple' o 'single'
			copyFirstNameToAll: false,
			customNamesBackup: null,
			atomicProcessing: true, // Por defecto true (procesamiento atómico)
		};
	}

	componentDidMount() {
		// Si hay archivos preseleccionados, notificar al componente padre
		if (this.props.preSelectedFiles && this.props.preSelectedFiles.length > 0) {
			if (this.props.onFilesSelected) {
				this.props.onFilesSelected(this.props.preSelectedFiles);
			}
		}

		// Si se fuerza un modo de carga, establecerlo
		if (this.props.forceUploadMode) {
			this.setState({ uploadMode: this.props.forceUploadMode });
		}

		// Cargar las carpetas si no están disponibles
		if (
			!this.props.storeFolders.items ||
			this.props.storeFolders.items.length === 0
		) {
			this.props.getStoreFolders();
		}
	}

	componentDidUpdate(prevProps) {
		// Si cambia el selectedTab, limpiar el estado
		if (prevProps.selectedTab !== this.props.selectedTab) {
			this.setState({
				uploadMode: null,
				files: [],
				customNames: {},
				copyFirstNameToAll: false,
				customNamesBackup: null,
				errors: {},
				selectedFolder: -1,
				employeeIdentifier: "NIF",
			});
		}
	}

	onDrop = (files) => {
		// Inicializar nombres personalizados para los nuevos archivos
		const customNames = { ...this.state.customNames };
		files.forEach((file) => {
			const fileKey = `${file.name}-${file.lastModified}`;
			// No inicializar con el nombre original, solo cuando el usuario lo modifique
			if (!customNames.hasOwnProperty(fileKey)) {
				customNames[fileKey] = "";
			}
		});

		this.setState({ files, customNames });
		// Notificar al componente padre sobre los archivos seleccionados
		if (this.props.onFilesSelected) {
			this.props.onFilesSelected(files);
		}
	};

	removeFile = (file) => {
		const { files, customNames } = this.state;
		const fileKey = `${file.name}-${file.lastModified}`;

		// Crear nueva copia de customNames sin el archivo eliminado
		const newCustomNames = { ...customNames };
		delete newCustomNames[fileKey];

		this.setState({
			files: files.filter((f) => f.lastModified !== file.lastModified),
			customNames: newCustomNames,
		});
	};

	// Nueva función para manejar cambios en nombres personalizados
	handleCustomNameChange = (file, newName) => {
		const { customNames, copyFirstNameToAll, files } = this.state;
		const fileKey = `${file.name}-${file.lastModified}`;

		// Actualizar el nombre del fichero editado
		let updatedCustomNames = {
			...customNames,
			[fileKey]: newName,
		};

		// Si está activo el switch y es el primer archivo, propagamos al resto
		const isFirstFile =
			files.length > 0 && files[0].lastModified === file.lastModified;
		if (copyFirstNameToAll && isFirstFile) {
			files.forEach((f, index) => {
				if (index === 0) return;
				const k = `${f.name}-${f.lastModified}`;
				updatedCustomNames[k] = newName;
			});
		}

		// Si el primer nombre se borra, apagar el switch para evitar que reaparezca encendido
		if (isFirstFile && (newName || "").trim() === "") {
			this.setState({
				customNames: updatedCustomNames,
				copyFirstNameToAll: false,
			});
			return;
		}

		this.setState({ customNames: updatedCustomNames });
	};

	// Propagar el nombre del primer archivo a todos
	propagateFirstNameToAll = () => {
		const { files, customNames } = this.state;
		if (!files || files.length === 0) return;
		const first = files[0];
		const firstKey = `${first.name}-${first.lastModified}`;
		const firstName = customNames[firstKey] || "";
		let updated = { ...customNames };
		files.forEach((f, index) => {
			if (index === 0) return;
			const k = `${f.name}-${f.lastModified}`;
			updated[k] = firstName;
		});
		this.setState({ customNames: updated });
	};

	// Toggle del switch de copia masiva
	handleToggleCopyFirst = () => {
		this.setState(
			(prev) => {
				const turningOn = !prev.copyFirstNameToAll;
				if (turningOn) {
					// Guardar respaldo antes de propagar
					return {
						copyFirstNameToAll: true,
						customNamesBackup: { ...prev.customNames },
					};
				} else {
					// Restaurar respaldo al desactivar
					return {
						copyFirstNameToAll: false,
						customNames: prev.customNamesBackup || prev.customNames,
						customNamesBackup: null,
					};
				}
			},
			() => {
				if (this.state.copyFirstNameToAll) {
					this.propagateFirstNameToAll();
				}
			}
		);
	};

	// Función para obtener el nombre final del archivo (nombre personalizado + extensión)
	getFinalFileName = (file) => {
		const { customNames } = this.state;
		const fileKey = `${file.name}-${file.lastModified}`;
		const customName = customNames[fileKey] || "";

		// Si no hay nombre personalizado, usar el nombre original
		if (!customName || customName.trim() === "") {
			return file.name;
		}

		// Extraer extensión del archivo original
		const lastDotIndex = file.name.lastIndexOf(".");
		const extension = lastDotIndex > 0 ? file.name.substring(lastDotIndex) : "";

		return customName + extension;
	};

	// Función para crear archivos con nombres personalizados
	createFilesWithCustomNames = () => {
		const { files } = this.state;
		return files.map((file) => {
			const finalName = this.getFinalFileName(file);
			// Crear un nuevo archivo con el nombre personalizado
			return new File([file], finalName, { type: file.type });
		});
	};

	handleSubmit = async (event) => {
		event.preventDefault();

		this.setState({ submitted: true });

		const { selectedFolder, files, employeeIdentifier, uploadMode } =
			this.state;
		const { employee, handleClose, user, onDocumentAdded, selectedTab } =
			this.props;
		const employeeId = employee ? employee.id : null;

		// Determinar si es envío masivo basado en el tab seleccionado
		const isMassiveUpload = selectedTab === "bulk";

		// Validación: siempre requiere carpeta y archivos
		if (selectedFolder === -1 || files.length === 0) {
			this.setState({ isLoading: false });
			return;
		}

		this.setState({ isLoading: true });

		try {
			let response;
			// Usar archivos con nombres personalizados
			const filesWithCustomNames = this.createFilesWithCustomNames();

			if (isMassiveUpload) {
				if (uploadMode === "single") {
					// Envío masivo de un solo archivo PDF
					try {
						// Para el envío masivo de un solo PDF, usar custom_name en lugar de modificar el filename
						const file = this.state.files[0];
						const fileKey = `${file.name}-${file.lastModified}`;
						let customName = this.state.customNames[fileKey] || "";

						// Extraer el nombre original sin extensión para comparar
						const lastDotIndex = file.name.lastIndexOf(".");
						const originalNameWithoutExt =
							lastDotIndex > 0
								? file.name.substring(0, lastDotIndex)
								: file.name;
						const extension =
							lastDotIndex > 0 ? file.name.substring(lastDotIndex) : "";

						// Solo enviar custom_name si el nombre ha sido modificado
						if (
							customName &&
							customName.trim() !== "" &&
							customName !== originalNameWithoutExt
						) {
							customName = customName + extension;
						} else {
							customName = ""; // No enviar custom_name si no se modificó
						}

						// Obtener atomic_processing del estado (true por defecto, false si el checkbox está marcado)
						const atomicProcessing = this.state.atomicProcessing;
						console.log(
							"atomicProcessing value:",
							atomicProcessing,
							"type:",
							typeof atomicProcessing
						);

						response = await documentsService.uploadMassiveSingleFile(
							selectedFolder,
							file,
							customName,
							atomicProcessing
						);

						const pageResults = extractPdfBulkPageResults(response);

						if (pageResults) {
							this.setState({
								isLoading: false,
								errors: {},
							});
							if (response.ok) {
								toastr.success(this.props.t("common.success"), this.props.t("documents.bulk.pdfProcessed"));
							} else {
								toastr.warning(
									this.props.t("documents.bulk.incomplete"),
									this.props.t("documents.bulk.checkPageDetails")
								);
							}
							response = pageResults;
						} else if (!response.ok) {
							// Manejar errores del backend sin detalle por página
							const formatted = errorFormating(response);
							const errorMessage =
								formatted?.general ||
								response?.errors?.[0]?.description ||
								this.props.t("documents.upload.errorProcessingPdf");
							toastr.error("Error", errorMessage);
							this.setState({
								isLoading: false,
								errors: formatted,
							});
							return;
						} else {
							throw new Error(this.props.t("documents.upload.unexpectedServerResponse"));
						}
					} catch (singleError) {
						console.error("Error en procesamiento de PDF:", singleError);
						const errorMessage =
							singleError?.response?.errors?.[0]?.description ||
							singleError?.message ||
							this.props.t("documents.upload.errorPdfInvalid");
						this.setState({
							isLoading: false,
							errors: { general: errorMessage },
						});
						toastr.error("Error", errorMessage);
						return;
					}
				} else {
					// Envío masivo de múltiples archivos
					try {
						response = await documentsService.uploadMassiveAttachments(
							selectedFolder,
							filesWithCustomNames,
							employeeIdentifier
						);

						if (Array.isArray(response)) {
							this.setState({
								isLoading: false,
								errors: {},
							});
							toastr.success(this.props.t("common.success"), this.props.t("documents.bulk.completed"));
						} else {
							throw new Error(this.props.t("documents.upload.unexpectedServerResponse"));
						}
					} catch (massiveError) {
						console.error("Error en envío masivo:", massiveError);
						this.setState({
							isLoading: false,
							errors: {
								general: this.props.t("documents.bulk.notAvailable"),
							},
						});
						toastr.error(
							"Error",
							this.props.t("documents.bulk.notAvailableShort")
						);
						return;
					}
				}
			} else {
				await documentsService.uploadAttachments(
					selectedFolder,
					employeeId,
					filesWithCustomNames
				);
				this.setState({
					isLoading: false,
					errors: {},
				});
				toastr.success(this.props.t("common.success"), this.props.t("documents.upload.documentsUploaded"));
			}

			trackHaddockDocumentUpload(user, {
				document_count: files.length,
				upload_mode: isMassiveUpload ? uploadMode || "bulk" : "standard",
				employee_id: employeeId,
				source: selectedTab === "bulk" ? "documents_bulk_tab" : "documents_page",
			});

			// Llamar al callback para refrescar la información
			if (onDocumentAdded) {
				const isBulkSinglePdf =
					isMassiveUpload && uploadMode === "single" && Array.isArray(response);
				if (isBulkSinglePdf) {
					onDocumentAdded(response, {
						loadDespiteErrors: !this.state.atomicProcessing,
					});
				} else {
					onDocumentAdded(response);
				}
			} else {
				console.error("❌ onDocumentAdded is not defined");
			}

			// Cerrar el modal para todos los tipos de envío
			setTimeout(() => {
				handleClose();
			}, 100);
		} catch (error) {
			console.error("Error general:", error);
			this.setState({
				isLoading: false,
				errors: errorFormating(error),
			});
			toastr.error("Error", this.props.t("documents.upload.errorUploading"));
		}
	};

	handleChange = (event) => {
		this.setState({
			selectedFolder: event.target.value,
		});
	};

	handleEmployeeIdentifierChange = (event) => {
		this.setState({
			employeeIdentifier: event.target.value,
		});
	};

	handleUploadModeSelect = (mode) => {
		// Si volvemos atrás (mode null), limpiamos todo el estado relevante
		if (mode === null) {
			this.setState({
				uploadMode: null,
				files: [],
				customNames: {},
				copyFirstNameToAll: false,
				customNamesBackup: null,
				errors: {},
				selectedFolder: -1,
				employeeIdentifier: "NIF",
			});
			return;
		}

		// Cambio entre 'single' y 'multiple': limpiamos archivos y nombres
		this.setState({
			uploadMode: mode,
			files: [],
			customNames: {},
			copyFirstNameToAll: false,
			customNamesBackup: null,
			errors: {},
		});
	};

	onDropSingle = (files) => {
		// Solo aceptar el primer archivo y verificar que sea PDF
		const file = files[0];
		if (file && file.type === "application/pdf") {
			// Inicializar nombre personalizado para el archivo PDF
			const customNames = { ...this.state.customNames };
			const fileKey = `${file.name}-${file.lastModified}`;
			// No inicializar con el nombre original, solo cuando el usuario lo modifique
			if (!customNames.hasOwnProperty(fileKey)) {
				customNames[fileKey] = "";
			}

			this.setState({
				files: [file],
				customNames,
				errors: {}, // Limpiar errores previos
			});
			if (this.props.onFilesSelected) {
				this.props.onFilesSelected([file]);
			}
		} else {
			toastr.error("Error", this.props.t("documents.upload.selectValidPdf"));
			this.setState({
				files: [],
				errors: { general: this.props.t("documents.upload.onlyPdf") },
			});
		}
	};

	checkValidField = (name) => {
		return this.state.submitted && this.state.selectedFolder == -1;
	};

	getDropzone = () => {
		// temporary dropzone files
		const files =
			this.state.files.length > 0 &&
			this.state.files.map((file, i) => {
				var size = file.size / 1000000;
				var sizeLetters = "Mb";

				if (size < 1) {
					size = file.size / 1000;
					sizeLetters = "Kb";
				}

				// Extraer extensión del archivo original
				const lastDotIndex = file.name.lastIndexOf(".");
				const extension =
					lastDotIndex > 0 ? file.name.substring(lastDotIndex) : "";
				const fileKey = `${file.name}-${file.lastModified}`;
				const customName = this.state.customNames[fileKey] || "";

				return (
					<li className="file-item-temp" key={i}>
						<div className="file-info" title={file.name}>
							<div className="file-name-section">
								<span className="file-name">{file.name}</span>
								<span className="file-size">
									({size.toFixed(2)}
									{sizeLetters})
								</span>
							</div>
							<div className="custom-name-section">
								<FormControl
									type="text"
									placeholder={this.props.t("documents.upload.customNamePlaceholder")}
									value={customName}
									onChange={(e) =>
										this.handleCustomNameChange(file, e.target.value)
									}
									className="custom-name-input"
								/>
								<span className="file-extension">{extension}</span>
							</div>
						</div>
						<div className="file-action">
							<button
								type="button"
								className="btn btn-icon"
								title={this.props.t("common.delete")}
								onClick={() => this.removeFile(file)}
							>
								<FiTrash2 />
							</button>
						</div>
					</li>
				);
			});

		return (
			<Dropzone onDrop={this.onDrop}>
				{({ getRootProps, getInputProps }) => (
					<section className="dropzone-container">
						<div {...getRootProps({ className: "dropzone" })}>
							<input {...getInputProps()} />
							<p>{this.props.t("documents.upload.dropzoneText")}</p>
						</div>
						{(() => {
							const showInBulkMultiple =
								this.props.selectedTab === "bulk" &&
								this.state.uploadMode === "multiple" &&
								this.state.files.length > 1;
							if (!showInBulkMultiple) return null;
							const first = this.state.files[0];
							const firstKey = `${first.name}-${first.lastModified}`;
							const firstCustom = (
								this.state.customNames[firstKey] || ""
							).trim();
							if (!firstCustom) return null;
							return (
								<FormGroup
									className="form-group form-group-flex init-all-wrapper mt-3"
									controlId="copy_first_name_all"
								>
									<FormLabel>
										{this.props.t("documents.upload.copyFirstNameToAll")}
									</FormLabel>
									<Switch
										controlId="copy_first_name_all"
										isOn={this.state.copyFirstNameToAll}
										handleToggle={this.handleToggleCopyFirst}
										className="switch-mini"
									/>
								</FormGroup>
							);
						})()}
						<ul
							className="dropzone-files"
							style={{
								maxHeight: "200px",
								overflowY: "auto",
								overflowX: "hidden",
							}}
						>
							{files}
						</ul>
					</section>
				)}
			</Dropzone>
		);
	};

	getSingleFileDropzone = () => {
		const files =
			this.state.files.length > 0 &&
			this.state.files.map((file, i) => {
				var size = file.size / 1000000;
				var sizeLetters = "Mb";

				if (size < 1) {
					size = file.size / 1000;
					sizeLetters = "Kb";
				}

				// Extraer extensión del archivo original
				const lastDotIndex = file.name.lastIndexOf(".");
				const extension =
					lastDotIndex > 0 ? file.name.substring(lastDotIndex) : "";
				const fileKey = `${file.name}-${file.lastModified}`;
				const customName = this.state.customNames[fileKey] || "";

				return (
					<li className="file-item-temp" key={i}>
						<div className="file-info" title={file.name}>
							<div className="file-name-section">
								<span className="file-name">{file.name}</span>
								<span className="file-size">
									({size.toFixed(2)}
									{sizeLetters})
								</span>
							</div>
							<div className="custom-name-section">
								<FormControl
									type="text"
									placeholder={this.props.t("documents.upload.customNamePlaceholder")}
									value={customName}
									onChange={(e) =>
										this.handleCustomNameChange(file, e.target.value)
									}
									className="custom-name-input"
								/>
								<span className="file-extension">{extension}</span>
							</div>
						</div>
						<div className="file-action">
							<button
								type="button"
								className="btn btn-icon"
								title={this.props.t("common.delete")}
								onClick={() => this.removeFile(file)}
							>
								<FiTrash2 />
							</button>
						</div>
					</li>
				);
			});

		return (
			<Dropzone onDrop={this.onDropSingle} accept=".pdf,application/pdf">
				{({ getRootProps, getInputProps }) => (
					<section className="dropzone-container">
						<div {...getRootProps({ className: "dropzone" })}>
							<input {...getInputProps()} />
							<p>
								{this.props.t("documents.upload.dropzoneSingleText")}
							</p>
							<small className="text-muted">{this.props.t("documents.upload.onlyPdf")}</small>
						</div>
						<ul
							className="dropzone-files"
							style={{
								maxHeight: "200px",
								overflowY: "auto",
								overflowX: "hidden",
							}}
						>
							{files}
						</ul>
					</section>
				)}
			</Dropzone>
		);
	};

	render() {
		const { storeFolders, selectedTab } = this.props;
		const { employeeIdentifier, uploadMode } = this.state;
		const appName = getAppName();

		// Determinar si es envío masivo basado en el tab seleccionado
		const isMassiveUpload = selectedTab === "bulk";

		// Ordenar las carpetas alfabéticamente
		const sortedFolders = storeFolders.items
			? [...storeFolders.items].sort((a, b) => a.name.localeCompare(b.name))
			: [];

		// Si es envío masivo y no se ha seleccionado modo, mostrar opciones
		if (isMassiveUpload && !uploadMode) {
			return (
				<div>
					<div className="modal-body modal-documentos">
						<p className="mb-4">{this.props.t("documents.bulk.selectType")}</p>

						<div className="row align-items-stretch">
							<div className="col-6 d-flex">
								<div
									className="upload-option-card"
									onClick={() => this.handleUploadModeSelect("multiple")}
								>
									<FaRegCopy size={20} className="card-icon" />
									<h5>{this.props.t("documents.bulk.multipleFiles")}</h5>
									<p>{this.props.t("documents.bulk.multipleFilesDesc", { appName })}</p>
								</div>
							</div>

							<div className="col-6 d-flex">
								<div
									className="upload-option-card"
									onClick={() => this.handleUploadModeSelect("single")}
								>
									<FaRegFilePdf size={20} className="card-icon" />
									<h5>{this.props.t("documents.bulk.singleFile")}</h5>
									<p>{this.props.t("documents.bulk.singleFileDesc", { appName })}</p>
								</div>
							</div>
						</div>
					</div>

					<div className="modal-footer">
						<button
							type="button"
							onClick={this.props.handleClose}
							className="btn btn-outline-primary"
						>
							{this.props.t("common.cancel")}
						</button>
					</div>
				</div>
			);
		}

		return (
			<form onSubmit={this.handleSubmit}>
				<div
					className="modal-body modal-documentos"
					style={{
						maxHeight: "70vh",
						overflowY: "auto",
					}}
				>
					{isMassiveUpload ? (
						<div>
							{uploadMode === "single" ? (
								<p className="intro-text">
									{this.props.t("documents.bulk.introSingle")}
									<br />
									<small>{this.props.t("documents.bulk.introSingleSmall")}</small>
								</p>
							) : (
								<p className="intro-text">
									{this.props.t("documents.bulk.introMultiple")}
									<br />
									<small>{this.props.t("documents.bulk.introMultipleSmall")}</small>
								</p>
							)}
							<div className="alert alert-warning mt-3">
								{this.props.t("documents.bulk.ocrWarning")}
							</div>
						</div>
					) : (
						<p className="intro-text">
							{this.props.t("documents.upload.introText", { appName })}
						</p>
					)}

					<FormGroup className="form-group" controlId="category">
						<FormLabel>
							{this.props.t("documents.folder.label")} <span className="label-required">*</span>
						</FormLabel>
						<FormControl
							onChange={this.handleChange}
							required
							value={this.state.selectedFolder}
							isInvalid={this.checkValidField("category")}
							as="select"
							disabled={storeFolders.loading}
						>
							<option value="-1">
								{storeFolders.loading
									? this.props.t("documents.folder.loading")
									: this.props.t("documents.folder.select")}
							</option>
							{sortedFolders.map((folder) => (
								<option key={folder.id} value={folder.id}>
									{folder.name}
								</option>
							))}
						</FormControl>

						<FormControl.Feedback type="invalid">
							{this.props.t("documents.folder.selectRequired")}
						</FormControl.Feedback>
					</FormGroup>

					{isMassiveUpload && uploadMode === "multiple" && (
						<FormGroup className="form-group" controlId="employeeIdentifier">
							<FormLabel>{this.props.t("documents.upload.employeeIdentifier")}</FormLabel>
							<FormControl
								as="select"
								value={employeeIdentifier}
								onChange={this.handleEmployeeIdentifierChange}
							>
								<option value="NIF">NIF</option>
								<option value="EMPLOYEE_CODE">{this.props.t("documents.upload.employeeCode")}</option>
							</FormControl>
							<small className="form-text text-muted">
								{this.props.t("documents.upload.employeeIdentifierHelp")}
							</small>
						</FormGroup>
					)}

					<label className="form-label">{this.props.t("documents.upload.documentsLabel")}</label>
					{isMassiveUpload && uploadMode === "single"
						? this.getSingleFileDropzone()
						: this.getDropzone()}

					{isMassiveUpload && uploadMode === "single" && (
						<FormGroup
							className="form-group mt-3 atomic-processing-switch"
							controlId="atomic_processing"
						>
							<FormGroup className="form-group-flex form-row-switch left">
								<FormLabel className="ml-0">
									{this.props.t("documents.upload.loadDespiteErrors")}
								</FormLabel>
								<Switch
									controlId="atomic_processing"
									isOn={!this.state.atomicProcessing}
									handleToggle={() =>
										this.setState({
											atomicProcessing: !this.state.atomicProcessing,
										})
									}
									className="switch-mini"
								/>
							</FormGroup>
							{/* <small className="form-text text-muted">
								Si está activado, se procesarán todos los documentos posibles aunque algunos fallen. Si está desactivado, se detendrá el procesamiento al primer error.
							</small> */}
						</FormGroup>
					)}

					{this.state.errors && this.state.errors.general && (
						<div className="alert alert-danger mt-3">
							{this.state.errors.general}
						</div>
					)}
				</div>

				<div className="modal-footer">
					<button
						type="button"
						onClick={this.props.handleClose}
						className="btn btn-outline-primary"
					>
						{this.props.t("common.cancel")}
					</button>
					{isMassiveUpload && uploadMode && (
						<button
							type="button"
							onClick={() => this.handleUploadModeSelect(null)}
							className="btn btn-outline-secondary mr-2"
						>
							{this.props.t("common.back")}
						</button>
					)}
					<SubmitButton
						type="submit"
						isLoading={this.state.isLoading}
						text={
							isMassiveUpload
								? uploadMode === "single"
									? this.props.t("documents.upload.processPdf")
									: this.props.t("documents.bulk.sendMassively")
								: this.props.t("common.save")
						}
						loadingText={
							isMassiveUpload
								? uploadMode === "single"
									? this.props.t("documents.upload.processingPdf")
									: this.props.t("documents.bulk.sending")
								: this.props.t("common.saving")
						}
					/>
				</div>
			</form>
		);
	}
}

const mapStateToProps = (state) => {
	return {
		storeFolders: state.storeFoldersReducer,
		user: state.authReducer.user,
	};
};

const mapDispatchToProps = (dispatch) => ({
	getStoreFolders: () => dispatch(storeFoldersActions.getStoreFolders()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(DocumentsAdd));
